import React, { createContext, useState, useContext, useEffect } from "react";
import axios from "../utils/axiosConfig";

const AuthContext = createContext(null);

const isDevelopment = process.env.NODE_ENV === 'development';
const debugLog = (...args) => {
  if (isDevelopment) {
    console.log(...args);
  }
};
const debugError = (...args) => {
  if (isDevelopment) {
    console.error(...args);
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    try {
      const storedUser = localStorage.getItem("user");
      return storedUser ? JSON.parse(storedUser) : null;
    } catch (error) {
      debugError("Error parsing stored user:", error);
      return null;
    }
  });
  const [loading, setLoading] = useState(true);

  const login = async (email, password) => {
    try {
      debugLog("Login attempt:", {
        email,
        passwordLength: password ? password.length : 0,
        url: "/api/users/login",
      });

      const response = await axios.post("/api/users/login", {
        email,
        password,
      });

      debugLog("Server response:", {
        status: response.status,
        hasData: !!response.data,
        hasToken: !!response.data?.token,
        hasUser: !!response.data?.user,
      });

      if (!response.data || !response.data.token || !response.data.user) {
        throw new Error("Ungültige Antwort vom Server");
      }

      const { token, user } = response.data;
      const userData = {
        _id: user.id || user._id,
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        isAdmin: !!user.isAdmin,
        username: user.username,
      };

      localStorage.setItem("token", token);
      localStorage.setItem("user", JSON.stringify(userData));
      setUser(userData);

      return true;
    } catch (error) {
      debugError("Login error details:", {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
      });
      throw new Error(
        error.response?.data?.message ||
          "Login fehlgeschlagen. Bitte überprüfen Sie ihre Eingaben."
      );
    }
  };
  const validateToken = async () => {
    const token = localStorage.getItem("token");
    const storedUser = localStorage.getItem("user");

    if (!token || !storedUser) {
      setUser(null);
      return false;
    }

    try {
      const response = await axios.get("/api/users/validate-token", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.success && response.data.user) {
        // Update user data with the latest from server
        const userData = {
          _id: response.data.user.id || response.data.user._id,
          firstName: response.data.user.firstName,
          lastName: response.data.user.lastName,
          email: response.data.user.email,
          isAdmin: !!response.data.user.isAdmin,
          username: response.data.user.username,
        };
        localStorage.setItem("user", JSON.stringify(userData));
        setUser(userData);
        return true;
      } else {
        throw new Error("Invalid token response");
      }
    } catch (error) {
      debugError("Token validation error:", error);
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      setUser(null);
      return false;
    }
  };

  useEffect(() => {
    const checkAuth = async () => {
      setLoading(true);
      await validateToken();
      setLoading(false);
    };

    checkAuth();

    const interval = setInterval(checkAuth, 30 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const register = async (firstName, lastName, username, email, password) => {
    try {
      const response = await axios.post("/api/users/register", {
        firstName,
        lastName,
        username:
          username || `${firstName.toLowerCase()}.${lastName.toLowerCase()}`,
        email,
        password,
      });
      if (response.data && response.data.token) {
        localStorage.setItem("token", response.data.token);

        if (response.data.user) {
          const userData = {
            _id: response.data.user.id || response.data.user._id,
            firstName: response.data.user.firstName,
            lastName: response.data.user.lastName,
            email: response.data.user.email,
            isAdmin: !!response.data.user.isAdmin,
            username: response.data.user.username,
          };
          localStorage.setItem("user", JSON.stringify(userData));
          setUser(userData);
        }
        return true;
      } else {
        throw new Error("Keine gültige Antwort vom Server erhalten");
      }
    } catch (error) {
      debugError("Registration error:", error.response?.data || error);

      if (error.response?.status === 400) {
        throw new Error(
          error.response.data?.message || "Ungültige Eingabedaten"
        );
      } else if (error.response?.status === 409) {
        throw new Error("Email oder Benutzername bereits vergeben");
      } else {
        throw new Error(
          "Registrierung fehlgeschlagen. Bitte versuchen Sie es später erneut."
        );
      }
    }
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, register, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
